// Vínculo contacto ↔ cuenta en ambos sentidos.
// - AccountContacts: dentro de la ficha de una CUENTA, lista sus contactos y
//   permite crear uno nuevo ya adjunto o vincular uno existente.
// - ContactAccount: dentro de la ficha de un CONTACTO, muestra su cuenta y
//   permite vincular / cambiar / quitar.
import type { JSX } from "react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, ExternalLink, FileText, Mail, Receipt, Star, Upload, UserPlus } from "lucide-react";
import { MODULES } from "../modules/registry.tsx";
import { useCreate, useList, useRecord, useUpdate, type Row } from "../lib/resource.ts";
import { useMe } from "../lib/auth.ts";
import { applyDynamicOptions, useCustomFields } from "../lib/customFields.ts";
import { buildPayload, RecordForm, validate, type Values } from "./RecordForm.tsx";
import { AsyncCombobox } from "./AsyncCombobox.tsx";
import { Button, Card, Modal } from "./ui.tsx";
import { toast } from "./toast.tsx";
import { ApiError, api } from "../lib/api.ts";

const userName = (r: Row): string =>
  `${String(r["firstName"] ?? "")} ${String(r["lastName"] ?? "")}`.trim() || String(r["email"] ?? "(sin nombre)");

const contactName = (r: Row): string =>
  `${String(r["firstName"] ?? "")} ${String(r["lastName"] ?? "")}`.trim() || "(sin nombre)";

// ────────── Contactos de una cuenta ──────────
export function AccountContacts({ accountId }: { accountId: string }): JSX.Element {
  const navigate = useNavigate();
  const list = useList("contacts", { pageSize: 100, extra: { accountId } });
  const create = useCreate("contacts", "contact");
  const update = useUpdate("contacts");
  const customFields = useCustomFields("contacts");
  const baseFields = MODULES["contacts"]?.fields ?? [];
  const formFields = [...applyDynamicOptions(baseFields, customFields.data ?? []), ...(customFields.data ?? [])];

  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState(false);
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const readOnly = Boolean(useMe().data?.readOnly);

  const rows = list.data?.rows ?? [];

  const submit = (): void => {
    const errs = validate(formFields, values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const body = { ...buildPayload(formFields, values), accountId } as Row;
    create.mutate(body, {
      onSuccess: () => { setAdding(false); setValues({}); toast.success("Contacto creado y vinculado."); void list.refetch(); },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear."),
    });
  };

  const linkExisting = (contactId: string): void => {
    if (!contactId) return;
    update.mutate({ id: contactId, body: { accountId } as Row }, {
      onSuccess: () => { setLinking(false); toast.success("Contacto vinculado."); void list.refetch(); },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo vincular."),
    });
  };

  const unlink = (contactId: string): void => {
    if (!window.confirm("¿Quitar este contacto de la cuenta? El contacto no se elimina.")) return;
    update.mutate({ id: contactId, body: { accountId: null } as Row }, {
      onSuccess: () => { toast.success("Contacto desvinculado."); void list.refetch(); },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo desvincular."),
    });
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-ink-secondary">Contactos <span className="text-ink-muted font-normal">({rows.length})</span></h3>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button small onClick={() => setLinking((v) => !v)}>Vincular existente</Button>
            <Button small variant="primary" onClick={() => { setValues({}); setErrors({}); setAdding(true); }}>+ Nuevo contacto</Button>
          </div>
        )}
      </div>

      {linking && (
        <div className="mb-4 p-3 bg-canvas rounded-md">
          <label className="label">Buscar contacto a vincular</label>
          <AsyncCombobox
            path="contacts"
            labelOf={(r) => `${contactName(r)}${r["email"] ? ` · ${String(r["email"])}` : ""}`}
            value=""
            onChange={(id) => linkExisting(id)}
            placeholder="Escribe un nombre o correo…"
          />
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted">Aún no hay contactos en esta cuenta.</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((r) => {
            const rol = (r["customData"] as Record<string, unknown> | undefined)?.["ROL"];
            return (
              <li key={String(r["id"])} className="flex items-center justify-between gap-3 py-2">
                <button className="text-left min-w-0" onClick={() => navigate(`/contacts/${String(r["id"])}`)}>
                  <div className="text-sm font-medium text-primary truncate">{contactName(r)}</div>
                  <div className="text-xs text-ink-muted truncate">
                    {[rol, r["title"], r["email"], r["phone"]].filter(Boolean).map(String).join(" · ") || "—"}
                  </div>
                </button>
                {!readOnly && <button className="text-xs text-ink-muted hover:text-red-600 shrink-0" onClick={() => unlink(String(r["id"]))}>Quitar</button>}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Nuevo contacto en esta cuenta"
        wide
        footer={
          <>
            <Button onClick={() => setAdding(false)}>Cancelar</Button>
            <Button variant="primary" onClick={submit} disabled={create.isPending}>
              {create.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </>
        }
      >
        <RecordForm fields={formFields} initial={values} errors={errors} onChange={setValues} />
      </Modal>
    </Card>
  );
}

// ────────── Cuenta de un contacto ──────────
export function ContactAccount({ contact }: { contact: Row }): JSX.Element {
  const navigate = useNavigate();
  const update = useUpdate("contacts");
  const [editing, setEditing] = useState(false);
  const readOnly = Boolean(useMe().data?.readOnly);
  const accountId = contact["accountId"] as string | null;
  const accountName = contact["accountName"] as string | undefined;

  const setAccount = (id: string | null): void => {
    update.mutate({ id: String(contact["id"]), body: { accountId: id } as Row }, {
      onSuccess: () => { setEditing(false); toast.success(id ? "Cuenta vinculada." : "Cuenta desvinculada."); },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo actualizar."),
    });
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ink-secondary">Cuenta</h3>
        {accountId && !editing && !readOnly && (
          <div className="flex items-center gap-2">
            <Button small onClick={() => setEditing(true)}>Cambiar</Button>
            <button className="text-xs text-ink-muted hover:text-red-600" onClick={() => setAccount(null)}>Quitar</button>
          </div>
        )}
      </div>

      {readOnly && !accountId ? (
        <p className="text-sm text-ink-muted">Sin cuenta vinculada.</p>
      ) : (editing || !accountId) && !readOnly ? (
        <div>
          <AsyncCombobox
            path="accounts"
            labelOf={(r) => String(r["accountName"] ?? "")}
            value={accountId ?? ""}
            {...(accountName ? { selectedLabel: accountName } : {})}
            onChange={(id) => setAccount(id || null)}
            placeholder="Buscar cuenta…"
          />
          {editing && <button className="mt-2 text-xs text-ink-muted hover:text-ink-secondary" onClick={() => setEditing(false)}>Cancelar</button>}
        </div>
      ) : (
        <button className="text-sm font-medium text-primary hover:underline" onClick={() => navigate(`/accounts/${accountId}`)}>
          {accountName ?? "Ver cuenta"}
        </button>
      )}
    </Card>
  );
}

// ────────── Equipo comercial de una cuenta ──────────
interface Assignee { userId: string; firstName: string; lastName: string; email: string }

export function AccountTeam({ accountId }: { accountId: string }): JSX.Element {
  const qc = useQueryClient();
  const rec = useRecord("accounts", accountId, "account");
  const canManage = rec.data?.access !== "read" && !useMe().data?.readOnly; // responsable/admin editan; equipo/consulta solo ven
  const ownerName = String(rec.data?.row?.["ownerName"] ?? "");

  const team = useQuery<{ rows: Assignee[] }>({
    queryKey: ["account-assignees", accountId],
    queryFn: () => api.get(`/accounts/${accountId}/assignees`),
  });
  const updateAccount = useUpdate("accounts");
  const [adding, setAdding] = useState(false);
  const [changingOwner, setChangingOwner] = useState(false);

  const refetch = (): void => { void team.refetch(); void qc.invalidateQueries({ queryKey: ["list", "accounts"] }); };

  const setOwner = (userId: string): void => {
    if (!userId) return;
    updateAccount.mutate({ id: accountId, body: { ownerId: userId } as Row }, {
      onSuccess: () => { setChangingOwner(false); toast.success("Responsable actualizado."); void rec.refetch(); refetch(); },
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo cambiar el responsable."),
    });
  };

  const add = (userId: string): void => {
    if (!userId) return;
    api.post(`/accounts/${accountId}/assignees`, { userId })
      .then(() => { setAdding(false); toast.success("Comercial asignado al equipo."); refetch(); })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : "No se pudo asignar."));
  };
  const remove = (userId: string): void => {
    api.del(`/accounts/${accountId}/assignees/${userId}`)
      .then(() => { toast.success("Comercial quitado del equipo."); refetch(); })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : "No se pudo quitar."));
  };

  const rows = team.data?.rows ?? [];

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-ink">Equipo comercial</h3>
        {canManage && <Button small onClick={() => setAdding((v) => !v)}><UserPlus size={15} strokeWidth={1.75} /> Asignar</Button>}
      </div>

      {/* Responsable principal */}
      {ownerName && (
        <div className="flex items-center gap-2.5 py-2 border-b border-line mb-1">
          <span className="w-8 h-8 rounded-full bg-primary text-white grid place-items-center text-xs font-semibold shrink-0">
            {ownerName.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-ink truncate">{ownerName}</div>
            <div className="text-xs text-ink-muted">Responsable</div>
          </div>
          {canManage && <button className="text-xs text-primary hover:underline shrink-0" onClick={() => setChangingOwner((v) => !v)}>Cambiar</button>}
          <Star size={15} className="text-accent shrink-0" fill="currentColor" />
        </div>
      )}

      {canManage && changingOwner && (
        <div className="my-3 p-3 bg-canvas rounded-md">
          <label className="label">Asignar nuevo responsable (comercial)</label>
          <AsyncCombobox path="comerciales" labelOf={(r) => userName(r)} value="" onChange={(id) => setOwner(id)} placeholder="Nombre o correo…" />
          <p className="text-xs text-ink-muted mt-1.5">Si la cuenta es Prospecto pasará a Cliente y se le avisará al comercial.</p>
        </div>
      )}

      {canManage && adding && (
        <div className="my-3 p-3 bg-canvas rounded-md">
          <label className="label">Buscar comercial a asignar</label>
          <AsyncCombobox
            path="comerciales"
            labelOf={(r) => userName(r)}
            value=""
            onChange={(id) => add(id)}
            placeholder="Nombre o correo…"
          />
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted mt-2">Sin comerciales adicionales.</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((a) => (
            <li key={a.userId} className="flex items-center gap-2.5 py-2">
              <span className="w-8 h-8 rounded-full bg-surface-hover text-ink-secondary grid place-items-center text-xs font-semibold shrink-0">
                {`${a.firstName?.[0] ?? ""}${a.lastName?.[0] ?? ""}`.toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ink truncate">{`${a.firstName} ${a.lastName}`.trim()}</div>
                <div className="text-xs text-ink-muted truncate">{a.email}</div>
              </div>
              {canManage && <button className="text-xs text-ink-muted hover:text-danger shrink-0" onClick={() => remove(a.userId)}>Quitar</button>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ────────── Correos con el contacto (Microsoft 365) ──────────
interface CtEmail { id: string; subject: string; date: string; fromName: string; fromAddress: string; to: string; preview: string; webLink: string; isRead: boolean; direction: "in" | "out" }

export function ContactEmails({ contactId }: { contactId: string }): JSX.Element {
  const q = useQuery<{ rows: CtEmail[]; reason?: string; message?: string; mailbox?: string }>({
    queryKey: ["contact-emails", contactId],
    queryFn: () => api.get(`/contacts/${contactId}/emails`),
    staleTime: 60_000,
    retry: false,
  });
  const data = q.data;
  const rows = data?.rows ?? [];

  const note = (): string | null => {
    if (!data) return null;
    if (data.reason === "not_configured") return "Conecta Microsoft 365 en Configuración → Servidor de correo para ver los correos.";
    if (data.reason === "no_email") return "Este contacto no tiene correo.";
    if (data.reason === "no_mailbox") return "El responsable no tiene una casilla de correo M365.";
    if (data.reason === "error") return "No se pudieron cargar los correos (revisa el permiso Mail.Read en Azure).";
    return null;
  };

  const fmt = (iso: string): string => {
    try { return new Date(iso).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-8 h-8 rounded-lg grid place-items-center bg-primary/[0.08] text-primary shrink-0"><Mail size={16} strokeWidth={1.75} /></span>
        <h3 className="text-sm font-semibold text-ink">Correos</h3>
        {data?.mailbox && <span className="text-xs text-ink-muted ml-auto truncate">buzón: {data.mailbox}</span>}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-ink-muted">Cargando correos…</p>
      ) : note() ? (
        <p className="text-sm text-ink-muted">{note()}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-muted">Sin correos recientes con este contacto.</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((m) => (
            <li key={m.id} className="py-2.5">
              <a href={m.webLink} target="_blank" rel="noreferrer" className="group block">
                <div className="flex items-center gap-2">
                  {m.direction === "in"
                    ? <ArrowDownLeft size={15} className="text-success shrink-0" />
                    : <ArrowUpRight size={15} className="text-primary shrink-0" />}
                  <span className={`text-sm truncate flex-1 ${m.isRead ? "text-ink" : "text-ink font-semibold"}`}>{m.subject}</span>
                  <span className="text-xs text-ink-muted shrink-0 tnum">{fmt(m.date)}</span>
                  <ExternalLink size={13} className="text-ink-muted opacity-0 group-hover:opacity-100 shrink-0" />
                </div>
                <div className="text-xs text-ink-muted truncate mt-0.5 pl-[23px]">
                  {m.direction === "in" ? `De: ${m.fromName || m.fromAddress}` : `Para: ${m.to}`} · {m.preview}
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ────────── Facturación electrónica de la cuenta (dirección + correo) ──────────
// Usa el endpoint dedicado /accounts/:id/billing: permite editar SOLO la
// facturación (incl. al perfil "Consulta" con can_manage_billing, que por lo
// demás es solo lectura). `canManage` viene del backend según el permiso real.
interface BillingData {
  billingStreet?: string | null; billingCity?: string | null; billingState?: string | null;
  billingZip?: string | null; billingCountry?: string | null; billingEmail?: string | null;
}
const BILLING_FIELDS: Array<{ key: keyof BillingData; label: string; placeholder?: string }> = [
  { key: "billingStreet", label: "Calle / dirección" },
  { key: "billingCity", label: "Ciudad" },
  { key: "billingState", label: "Departamento" },
  { key: "billingZip", label: "Código postal" },
  { key: "billingCountry", label: "País" },
  { key: "billingEmail", label: "Correo(s) de facturación", placeholder: "facturacion@empresa.com, contabilidad@empresa.com" },
];

export function AccountBilling({ accountId }: { accountId: string }): JSX.Element {
  const qc = useQueryClient();
  const bill = useQuery<{ billing: BillingData; canManage: boolean }>({
    queryKey: ["account-billing", accountId],
    queryFn: () => api.get(`/accounts/${accountId}/billing`),
  });
  const canManage = bill.data?.canManage ?? false;
  const b = bill.data?.billing;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<BillingData>({});
  const save = useMutation({
    mutationFn: (body: BillingData) => api.patch(`/accounts/${accountId}/billing`, body as Row),
    onSuccess: () => {
      setEditing(false);
      toast.success("Facturación actualizada.");
      void qc.invalidateQueries({ queryKey: ["account-billing", accountId] });
      void qc.invalidateQueries({ queryKey: ["record", "accounts", accountId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });

  const startEdit = (): void => { setDraft({ ...(b ?? {}) }); setEditing(true); };
  const emails = String(b?.billingEmail ?? "").split(/[;,]\s*/).map((s) => s.trim()).filter(Boolean);
  const addressLine = [b?.billingStreet, b?.billingCity, b?.billingState, b?.billingZip, b?.billingCountry]
    .map((v) => String(v ?? "").trim()).filter(Boolean).join(", ");
  const hasData = Boolean(addressLine) || emails.length > 0;

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-8 h-8 rounded-lg grid place-items-center bg-primary/[0.08] text-primary shrink-0"><Receipt size={16} strokeWidth={1.75} /></span>
        <h3 className="text-sm font-semibold text-ink">Facturación electrónica</h3>
      </div>

      {bill.isLoading ? (
        <p className="text-sm text-ink-muted">Cargando…</p>
      ) : editing ? (
        <div className="space-y-2.5">
          {BILLING_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="dt-label">{f.label}</label>
              <input
                className="input w-full mt-1"
                value={String(draft[f.key] ?? "")}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                {...(f.placeholder ? { placeholder: f.placeholder } : {})}
              />
            </div>
          ))}
          <p className="text-xs text-ink-muted">El correo admite varios separados por coma.</p>
          <div className="flex gap-2 pt-1">
            <Button small variant="primary" onClick={() => save.mutate(draft)} disabled={save.isPending}>{save.isPending ? "Guardando…" : "Guardar"}</Button>
            <Button small onClick={() => setEditing(false)}>Cancelar</Button>
          </div>
        </div>
      ) : hasData ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            {addressLine && (<div><div className="dt-label">Dirección de facturación</div><div className="text-sm text-ink mt-0.5">{addressLine}</div></div>)}
            {emails.length > 0 && (
              <div>
                <div className="dt-label">Correo de recepción</div>
                <div className="mt-0.5 space-y-0.5">{emails.map((m) => <a key={m} className="link block text-sm truncate" href={`mailto:${m}`}>{m}</a>)}</div>
              </div>
            )}
          </div>
          {canManage && <button className="text-xs text-primary hover:underline shrink-0" onClick={startEdit}>Editar</button>}
        </div>
      ) : canManage ? (
        <Button small variant="primary" onClick={startEdit}>
          <Mail size={15} strokeWidth={1.75} /> Agregar facturación
        </Button>
      ) : (
        <p className="text-sm text-ink-muted">Sin datos de facturación.</p>
      )}
    </Card>
  );
}

// ────────── RUT de la cuenta (subir / descargar — sin borrar) ──────────
interface RutMeta { exists: boolean; canManage?: boolean; filename?: string; sizeBytes?: number; uploadedAt?: string }

export function AccountRut({ accountId }: { accountId: string }): JSX.Element {
  const meta = useQuery<RutMeta>({ queryKey: ["account-rut", accountId], queryFn: () => api.get(`/accounts/${accountId}/rut/meta`) });
  const canManage = meta.data?.canManage ?? false;
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onFile = (file: File): void => {
    setUploading(true);
    const r = new FileReader();
    r.onload = () => {
      const base64 = String(r.result).split(",")[1] ?? "";
      api.post(`/accounts/${accountId}/rut`, { filename: file.name, contentType: file.type || "application/octet-stream", dataBase64: base64 })
        .then(() => { toast.success("RUT cargado."); void meta.refetch(); })
        .catch((e) => toast.error(e instanceof ApiError ? e.message : "No se pudo subir el RUT."))
        .finally(() => { setUploading(false); if (inputRef.current) inputRef.current.value = ""; });
    };
    r.onerror = () => { setUploading(false); toast.error("No se pudo leer el archivo."); };
    r.readAsDataURL(file);
  };

  const fmtSize = (b?: number): string => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  const d = meta.data;

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-8 h-8 rounded-lg grid place-items-center bg-primary/[0.08] text-primary shrink-0"><FileText size={16} strokeWidth={1.75} /></span>
        <h3 className="text-sm font-semibold text-ink">RUT</h3>
      </div>

      <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,image/*,application/pdf" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />

      {meta.isLoading ? (
        <p className="text-sm text-ink-muted">Cargando…</p>
      ) : d?.exists ? (
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-ink truncate">{d.filename}</div>
            <div className="text-xs text-ink-muted">{fmtSize(d.sizeBytes)}{d.uploadedAt ? ` · ${new Date(d.uploadedAt).toLocaleDateString("es-CO")}` : ""}</div>
          </div>
          <a href={`/api/v1/accounts/${accountId}/rut`} target="_blank" rel="noreferrer" className="btn-secondary btn-sm shrink-0"><ExternalLink size={15} strokeWidth={1.75} /> Abrir</a>
          {canManage && <Button small onClick={() => inputRef.current?.click()} disabled={uploading}>{uploading ? "Subiendo…" : "Reemplazar"}</Button>}
        </div>
      ) : canManage ? (
        <div>
          <Button small variant="primary" onClick={() => inputRef.current?.click()} disabled={uploading}>
            <Upload size={15} strokeWidth={1.75} /> {uploading ? "Subiendo…" : "Subir RUT"}
          </Button>
          <p className="text-xs text-ink-muted mt-2">PDF o imagen. Una vez cargado se puede descargar (no se elimina).</p>
        </div>
      ) : (
        <p className="text-sm text-ink-muted">Aún no se ha cargado el RUT.</p>
      )}
    </Card>
  );
}
